import { useEffect } from 'react';
import { DATASET_SYNC_RECOVERY_EVENT } from '@/local-sync/datasetSync';
import { hasStoredAuthSession } from '@/services/ApiService';

/**
 * Run `refetch` when the service comes back after an outage.
 *
 * A stable deploy restarts the backend under every open tab, so a load that runs once at mount
 * fails for a reason that resolves itself seconds later and then nothing puts it right. The work-area
 * switcher held that failure until somebody noticed a small red banner and pressed retry, or
 * reloaded — which read as "I cannot select any work area".
 *
 * `MaintenanceNotice` already dispatches this event the moment it sees the service answer again;
 * this is the same recovery the local-sync datasets get.
 *
 * I-1589 (DM-18): not in a web view with no stored session. The shell sends `foreground` and
 * `refresh` to every web view it holds, a signed-out one (the iPhone app's spare, a tab whose session
 * another web view ended) included, and each re-read went to an authenticated route and was
 * refused 401, again each time, for as long as the app was used.
 */
export function useRefetchOnServiceRecovery(refetch: () => void): void {
  useEffect(() => {
    const handler = () => {
      if (hasStoredAuthSession()) refetch();
    };
    window.addEventListener(DATASET_SYNC_RECOVERY_EVENT, handler);
    return () => window.removeEventListener(DATASET_SYNC_RECOVERY_EVENT, handler);
  }, [refetch]);
}
